import { ExpandMoreRounded } from "@mui/icons-material";
import {
    Box,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import { formatNumber, shortId } from "../KboFlowDebug/KboFlowDebugUI";
import { GroundingChip } from "./KboFlowRunDetailPrimitives";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";
import {
    DigestBreakdownRow,
    DigestContext,
    DigestRiskTrendRow,
    asArray,
    asNumber,
    asRecord,
    asString,
    formatClockDuration,
    formatCompactDate,
    formatPercent,
    formatRunDate,
    getBreakdownRows,
    getClassifierList,
    getHierarchyList,
    getModelDistributions,
    getModelTrends,
    getPermissionsSummary,
    getProgramEventsInWindow,
    getRiskSignals,
    getTextItems,
    getTopicMomentum,
} from "./KboFlowRunDigestModels";

/**
 * Renderers for the context assembly step: the collapsible bulleted list of
 * everything the run handed the model, including the full signal snapshot.
 */

function ScrollBox({
    maxHeight = 260,
    children,
}: {
    maxHeight?: number;
    children: React.ReactNode;
}) {
    return (
        <Box
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                maxHeight,
                overflowY: "auto",
            }}
        >
            {children}
        </Box>
    );
}

function BreakdownTable({
    rows,
    nameLabel = "Name",
}: {
    rows: DigestBreakdownRow[];
    nameLabel?: string;
}) {
    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No data recorded.
            </Typography>
        );
    }
    return (
        <ScrollBox>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>
                            {nameLabel}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                            Count
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                            Eddy rate
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                            Change
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`${row.name}-${index}`}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell align="right">
                                {formatNumber(row.count)}
                            </TableCell>
                            <TableCell align="right">
                                {formatPercent(row.eddyRate)}
                            </TableCell>
                            <TableCell align="right">
                                {typeof row.changePercent === "number"
                                    ? `${
                                          row.changePercent > 0 ? "+" : ""
                                      }${row.changePercent.toFixed(1)}%`
                                    : "—"}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </ScrollBox>
    );
}

function RiskTrendTable({ rows }: { rows: DigestRiskTrendRow[] }) {
    return (
        <ScrollBox maxHeight={200}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                            Count
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                            Prevalence
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                            Eddy rate
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`${row.bucket}-${index}`}>
                            <TableCell>{row.bucket}</TableCell>
                            <TableCell align="right">
                                {formatNumber(row.count)}/
                                {formatNumber(row.totalCount)}
                            </TableCell>
                            <TableCell align="right">
                                {formatPercent(row.prevalenceRate)}
                            </TableCell>
                            <TableCell align="right">
                                {formatPercent(row.eddyRate)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </ScrollBox>
    );
}

export function ContextSectionItem({
    title,
    meta,
    note,
    defaultExpanded = false,
    children,
}: {
    title: string;
    meta?: React.ReactNode;
    note?: string;
    defaultExpanded?: boolean;
    children?: React.ReactNode;
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const hasDetail = !!children;
    return (
        <Box
            sx={{ borderBottom: "1px solid", borderColor: "divider", py: 1.25 }}
        >
            <Box
                component={hasDetail ? "button" : "div"}
                type={hasDetail ? "button" : undefined}
                onClick={hasDetail ? () => setExpanded((v) => !v) : undefined}
                sx={{
                    alignItems: "center",
                    bgcolor: "transparent",
                    border: 0,
                    cursor: hasDetail ? "pointer" : "default",
                    display: "flex",
                    font: "inherit",
                    gap: 1,
                    p: 0,
                    textAlign: "left",
                    width: "100%",
                }}
            >
                {hasDetail ? (
                    <ExpandMoreRounded
                        sx={{
                            color: pageColors.muted,
                            flex: "0 0 auto",
                            fontSize: 18,
                            transform: expanded
                                ? "rotate(180deg)"
                                : "rotate(-90deg)",
                            transition: "transform 120ms",
                        }}
                    />
                ) : (
                    <Box
                        component="span"
                        sx={{
                            color: pageColors.muted,
                            flex: "0 0 auto",
                            px: "3px",
                        }}
                    >
                        •
                    </Box>
                )}
                <Typography
                    variant="body2"
                    sx={{ color: pageColors.ink, flex: 1, fontWeight: 700 }}
                >
                    {title}
                </Typography>
                {meta}
            </Box>
            {note && (
                <Typography
                    variant="body2"
                    sx={{ color: pageColors.orange, mt: 0.5, ml: 3.25 }}
                >
                    {note}
                </Typography>
            )}
            {hasDetail && (
                <Collapse in={expanded} timeout={150}>
                    <Box sx={{ mt: 1.25, pl: 3.25 }}>{children}</Box>
                </Collapse>
            )}
        </Box>
    );
}

function contextSectionSubheading(text: string) {
    return (
        <Typography
            variant="caption"
            sx={{
                color: pageColors.muted,
                display: "block",
                fontWeight: 800,
                letterSpacing: "0.04em",
                mt: 2,
                textTransform: "uppercase",
            }}
        >
            {text}
        </Typography>
    );
}

/** Detail content for a single Context assembly bullet row, keyed by normalized section name. Returns undefined for unrecognized/empty sections (no expand affordance shown). */
export function renderContextSectionDetail(
    key: string,
    context: DigestContext,
): React.ReactNode | undefined {
    switch (key) {
        case "tenantprofile": {
            const tenantContext = asRecord(context.tenantProfile?.context);
            const clientType = asString(tenantContext?.clientType);
            const goals = asString(
                asRecord(tenantContext?.goalsAndPriorities)?.value,
            );
            const programEvents = getProgramEventsInWindow(context);
            if (!clientType && !goals && programEvents.length === 0)
                return undefined;
            return (
                <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                    {clientType && (
                        <Typography
                            variant="body2"
                            sx={{ color: pageColors.muted }}
                        >
                            Client type: <strong>{clientType}</strong>
                        </Typography>
                    )}
                    {goals && (
                        <ScrollBox maxHeight={220}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: pageColors.muted,
                                    lineHeight: 1.6,
                                    p: 1.5,
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {goals}
                            </Typography>
                        </ScrollBox>
                    )}
                    <Box>
                        {contextSectionSubheading("Program events")}
                        {programEvents.length === 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                No program events recorded in this window.
                            </Typography>
                        ) : (
                            programEvents.map((event, index) => (
                                <Box
                                    key={event.id ?? index}
                                    sx={{ display: "flex", gap: 1, mt: 0.75 }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            flex: "0 0 auto",
                                            fontWeight: 800,
                                        }}
                                    >
                                        {formatCompactDate(event.startDate)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: pageColors.muted }}
                                    >
                                        {event.description}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            );
        }
        case "userprofile": {
            const userContext = asRecord(context.userProfile?.context);
            const role = asString(userContext?.role);
            const groups = [
                { label: "Goals", items: getTextItems(userContext, "goals") },
                {
                    label: "Responsibilities",
                    items: getTextItems(userContext, "responsibilities"),
                },
                {
                    label: "Success metrics",
                    items: getTextItems(userContext, "successMetrics"),
                },
                {
                    label: "Current concerns",
                    items: getTextItems(userContext, "currentConcerns"),
                },
            ].filter((group) => group.items.length > 0);
            if (!role && groups.length === 0) return undefined;
            return (
                <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                    {role && (
                        <Typography
                            variant="body2"
                            sx={{ color: pageColors.muted }}
                        >
                            Role: <strong>{role}</strong>
                        </Typography>
                    )}
                    {groups.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No goals, responsibilities, or concerns are on file
                            for this recipient.
                        </Typography>
                    ) : (
                        groups.map((group) => (
                            <Box key={group.label}>
                                {contextSectionSubheading(group.label)}
                                {group.items.map((item, index) => (
                                    <Typography
                                        key={item.id ?? index}
                                        variant="body2"
                                        sx={{
                                            color: pageColors.muted,
                                            mt: 0.35,
                                        }}
                                    >
                                        • {item.text}
                                    </Typography>
                                ))}
                            </Box>
                        ))
                    )}
                </Box>
            );
        }
        case "permissions": {
            const { isUltraUser, effectivePermissions } = getPermissionsSummary(
                context.permissions,
            );
            if (effectivePermissions.length === 0) return undefined;
            return (
                <Box>
                    <Typography
                        variant="body2"
                        sx={{ color: pageColors.muted, mb: 1 }}
                    >
                        Ultra user:{" "}
                        <strong>{isUltraUser ? "Yes" : "No"}</strong>
                    </Typography>
                    <ScrollBox>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800 }}>
                                        Domain
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>
                                        Action
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>
                                        Resource
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {effectivePermissions.map((perm, index) => (
                                    <TableRow key={perm.id ?? index}>
                                        <TableCell>
                                            {perm.domain ?? "—"}
                                        </TableCell>
                                        <TableCell>
                                            {perm.action ?? "—"}
                                        </TableCell>
                                        <TableCell>
                                            {perm.resource ?? "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </ScrollBox>
                </Box>
            );
        }
        case "signalsnapshot": {
            const signalSnapshot = context.signalSnapshot;
            const baseline = asRecord(signalSnapshot?.baseline);
            const totalConversations = asNumber(baseline?.totalConversations);
            const topics = getBreakdownRows(signalSnapshot, "topics");
            const themes = getBreakdownRows(signalSnapshot, "themes");
            const classifiers = getBreakdownRows(signalSnapshot, "classifiers");
            const contactTypes = getBreakdownRows(
                signalSnapshot,
                "contactTypes",
            );
            const journeyStages = getBreakdownRows(
                signalSnapshot,
                "journeyStages",
            );
            const modelTrends = getModelTrends(signalSnapshot);
            const { trending, onTheRise } = getTopicMomentum(signalSnapshot);
            const { safetyEvents, hipaaCompliance } =
                getRiskSignals(signalSnapshot);
            const modelDistributions = getModelDistributions(signalSnapshot);
            const qualityInsights = asRecord(signalSnapshot?.qualityInsights);
            const questionScores = asArray(qualityInsights?.questionScores);
            const moduleScores = asArray(qualityInsights?.moduleScores);

            return (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                        variant="body2"
                        sx={{ color: pageColors.muted }}
                    >
                        {formatNumber(totalConversations)} conversations ·{" "}
                        {formatPercent(asNumber(baseline?.eddyRate))} eddy · avg{" "}
                        {formatClockDuration(
                            asString(baseline?.averageCallDuration),
                        )}
                    </Typography>

                    {contextSectionSubheading("Topics")}
                    <Box sx={{ mt: 0.75 }}>
                        <BreakdownTable rows={topics} nameLabel="Topic" />
                    </Box>

                    {themes.length > 0 && (
                        <>
                            {contextSectionSubheading("Themes")}
                            <Box sx={{ mt: 0.75 }}>
                                <BreakdownTable
                                    rows={themes}
                                    nameLabel="Theme"
                                />
                            </Box>
                        </>
                    )}

                    {contextSectionSubheading("Topic momentum — trending")}
                    <Box sx={{ mt: 0.75 }}>
                        {trending.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No trending topics.
                            </Typography>
                        ) : (
                            <ScrollBox>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800 }}>
                                                Topic
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: 800 }}
                                            >
                                                Volume
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: 800 }}
                                            >
                                                Share
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: 800 }}
                                            >
                                                Change
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {trending.map((t, i) => (
                                            <TableRow key={`${t.name}-${i}`}>
                                                <TableCell>
                                                    {t.name}
                                                    {t.isNew ? " (new)" : ""}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumber(t.volume)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatPercent(t.share, 1)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {typeof t.changePercent ===
                                                    "number"
                                                        ? `${
                                                              t.changePercent >
                                                              0
                                                                  ? "+"
                                                                  : ""
                                                          }${t.changePercent.toFixed(
                                                              1,
                                                          )}%`
                                                        : "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ScrollBox>
                        )}
                    </Box>

                    {onTheRise.length > 0 && (
                        <>
                            {contextSectionSubheading("Topics on the rise")}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                    mt: 0.75,
                                }}
                            >
                                {onTheRise.map((t, i) => (
                                    <GroundingChip
                                        key={`${t.name}-${i}`}
                                        signal={{
                                            kind: "topic",
                                            name: t.name,
                                            volume: t.volume,
                                            changePercent: t.changePercent,
                                        }}
                                    />
                                ))}
                            </Box>
                        </>
                    )}

                    {contextSectionSubheading("Classifiers")}
                    <Box sx={{ mt: 0.75 }}>
                        <BreakdownTable
                            rows={classifiers}
                            nameLabel="Classifier"
                        />
                    </Box>

                    {contextSectionSubheading("Model trends")}
                    <Box sx={{ mt: 0.75 }}>
                        <BreakdownTable
                            rows={modelTrends.map((m) => ({
                                name: m.name,
                                count: m.count,
                                eddyCount: m.eddyCount,
                                eddyRate: m.eddyRate,
                            }))}
                            nameLabel="Model"
                        />
                    </Box>

                    {contextSectionSubheading("Contact types")}
                    <Box sx={{ mt: 0.75 }}>
                        <BreakdownTable
                            rows={contactTypes}
                            nameLabel="Contact type"
                        />
                    </Box>

                    {contextSectionSubheading("Journey stages")}
                    <Box sx={{ mt: 0.75 }}>
                        <BreakdownTable
                            rows={journeyStages}
                            nameLabel="Journey stage"
                        />
                    </Box>

                    {contextSectionSubheading("Quality insights")}
                    <Box sx={{ mt: 0.75 }}>
                        {questionScores.length === 0 &&
                        moduleScores.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No quality insights recorded.
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {questionScores.length} question score
                                {questionScores.length === 1 ? "" : "s"} ·{" "}
                                {moduleScores.length} module score
                                {moduleScores.length === 1 ? "" : "s"}
                            </Typography>
                        )}
                    </Box>

                    {(safetyEvents.length > 0 ||
                        hipaaCompliance.length > 0) && (
                        <>
                            {contextSectionSubheading("Risk signals")}
                            <Box
                                sx={{
                                    display: "grid",
                                    gap: 1.25,
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "repeat(2, 1fr)",
                                    },
                                    mt: 0.75,
                                }}
                            >
                                {safetyEvents.length > 0 && (
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 700, mb: 0.5 }}
                                        >
                                            Safety events over time
                                        </Typography>
                                        <RiskTrendTable rows={safetyEvents} />
                                    </Box>
                                )}
                                {hipaaCompliance.length > 0 && (
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 700, mb: 0.5 }}
                                        >
                                            HIPAA compliance over time
                                        </Typography>
                                        <RiskTrendTable
                                            rows={hipaaCompliance}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}

                    {modelDistributions.length > 0 && (
                        <>
                            {contextSectionSubheading(
                                "Model result distributions",
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    mt: 0.75,
                                }}
                            >
                                {modelDistributions.map((dist, index) => (
                                    <Typography
                                        key={dist.modelName + index}
                                        variant="body2"
                                        sx={{ color: pageColors.muted }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                color: pageColors.ink,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {dist.modelName}:
                                        </Box>{" "}
                                        {dist.results
                                            .map(
                                                (r) =>
                                                    `${r.label}=${formatNumber(
                                                        r.count,
                                                    )}${
                                                        typeof r.share ===
                                                        "number"
                                                            ? ` (${formatPercent(
                                                                  r.share,
                                                                  1,
                                                              )})`
                                                            : ""
                                                    }`,
                                            )
                                            .join(", ")}
                                    </Typography>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            );
        }
        case "classifiercatalog":
        case "modelcatalog": {
            const classifiers = getClassifierList(context.classifierCatalog);
            if (classifiers.length === 0) return undefined;
            return (
                <ScrollBox maxHeight={320}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800 }}>
                                    Name
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>
                                    Type
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>
                                    Family
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>
                                    Purpose
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {classifiers.map((c, index) => (
                                <TableRow key={c.id ?? index}>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {c.name}
                                    </TableCell>
                                    <TableCell>{c.type ?? "—"}</TableCell>
                                    <TableCell>{c.family ?? "—"}</TableCell>
                                    <TableCell sx={{ color: pageColors.muted }}>
                                        {c.purpose ?? "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ScrollBox>
            );
        }
        case "hierarchies": {
            const hierarchies = getHierarchyList(context.hierarchies);
            if (hierarchies.length === 0) return undefined;
            return (
                <ScrollBox maxHeight={220}>
                    <Box sx={{ p: 1 }}>
                        {hierarchies.map((h, index) => (
                            <Typography
                                key={h.id ?? index}
                                variant="body2"
                                sx={{ color: pageColors.muted, py: 0.35 }}
                            >
                                {h.name}
                                {h.path && h.path !== h.name
                                    ? ` (${h.path})`
                                    : ""}
                            </Typography>
                        ))}
                    </Box>
                </ScrollBox>
            );
        }
        case "generationhistory": {
            const priorRuns = context.generationHistory?.priorRuns ?? [];
            if (priorRuns.length === 0) return undefined;
            return (
                <ScrollBox maxHeight={320}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            p: 1.5,
                        }}
                    >
                        {priorRuns.map((run, runIndex) => (
                            <Box key={run.runId ?? runIndex}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {formatRunDate(run.runCompletedAtUtc)} ·{" "}
                                    {shortId(run.runId)}
                                </Typography>
                                {(run.questions ?? []).map((q, qIndex) => (
                                    <Box
                                        key={q.questionId ?? qIndex}
                                        sx={{ mt: 0.5 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ color: pageColors.ink }}
                                        >
                                            [{q.outcome ?? "Unknown"}] {q.text}
                                        </Typography>
                                        {(q.resolutionSummary ||
                                            q.rejectionReason) && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: pageColors.muted,
                                                    mt: 0.15,
                                                }}
                                            >
                                                {q.resolutionSummary ??
                                                    q.rejectionReason}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </ScrollBox>
            );
        }
        default:
            return undefined;
    }
}
